import * as React from 'react';
import MaterialTable, { Localization, MaterialTableProps } from 'material-table';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import CustomTableRow from './components/customTableRow';
import CustomTableToolbar from './components/customTableToolbar';
import TableIcons from './icons';
import { createMuiTheme, Paper, ThemeOptions } from '@material-ui/core';
import palette from 'styles/palette';
import { MuiThemeProvider } from '@material-ui/core/styles';
import { CSSProperties } from 'react';

export interface AlteredMaterialTableProps<T extends object> extends MaterialTableProps<T> {
  onTreeExpandClick?: (data: T, isTreeExpanded: boolean) => void;
  selectAllComponent?: JSX.Element;
  noContentMessage?: string;
  fullWidth?: boolean;
  nested?: boolean;
  disableBorders?: boolean;
  tableRootStyle?: CSSProperties;
}

class AlteredMaterialTable<T extends object> extends React.Component<
  AlteredMaterialTableProps<T> & WrappedComponentProps,
  {}
> {
  private localization: Localization;
  private expandedTrees: number[];

  constructor(props) {
    super(props);

    this.expandedTrees = [];
    this.localization = {
      header: {
        actions: ''
      },
      body: {
        editTooltip: props.intl.formatMessage({ id: 'edit' }),
        deleteTooltip: props.intl.formatMessage({ id: 'data_table.delete' }),
        emptyDataSourceMessage:
          props.noContentMessage ||
          props.intl.formatMessage({ id: 'settings.content.noRegistrationPoints' }),
        editRow: {
          cancelTooltip: props.intl.formatMessage({ id: 'base.cancel' }),
          saveTooltip: props.intl.formatMessage({ id: 'base.ok' })
        }
      },
      pagination: {
        previousTooltip: props.intl.formatMessage({ id: 'settings.content.table.prevPage' }),
        nextTooltip: props.intl.formatMessage({ id: 'settings.content.table.nextPage' }),
        labelRowsSelect: props.intl.formatMessage({ id: 'settings.content.table.rows' })
      },
      toolbar: {
        searchTooltip: props.intl.formatMessage({ id: 'search' }),
        searchPlaceholder: props.intl.formatMessage({ id: 'search' })
      }
    };
  }

  /**
   * Clicking on an arrow that expands/collapses the row to show/hide its children rows will trigger this callback
   *
   * @param { data }
   * @param { isTreeExpanded }
   * */
  onTreeExpandClick = (data, isTreeExpanded: boolean) => {
    if (!isTreeExpanded && this.expandedTrees.indexOf(data.id) === -1) {
      this.expandedTrees.push(data.id);
    } else if (isTreeExpanded && this.expandedTrees.indexOf(data.id) !== -1) {
      this.expandedTrees.splice(this.expandedTrees.indexOf(data.id), 1);
      data.tableData.childRows.length && this.collapseChildrenRows(data.tableData.childRows);
    }
    this.props.onTreeExpandClick && this.props.onTreeExpandClick(data, isTreeExpanded);
  };

  /**
   * Collapse the children rows found under a specific row that has just been closed.
   * This is a 'nice to have', so that it resets the view when you, for example,
   * close the root node, after expanding all its child nodes.
   *
   * @param { data }
   * */
  collapseChildrenRows(data) {
    if (!this.props.nested) {
      return;
    }

    data.map((child) => {
      this.expandedTrees.indexOf(child.id) !== -1 &&
        this.expandedTrees.splice(this.expandedTrees.indexOf(child.id), 1);

      if (child.tableData.isTreeExpanded) {
        this.props.tableRef.current.dataManager.changeTreeExpand(child.tableData.path);
      }

      if (child.tableData.childRows && child.tableData.childRows.length) {
        this.collapseChildrenRows(child.tableData.childRows);
      }
    });
  }

  /**
   * Override the default MT table toolbar with a custom one, and render it.
   *
   * @param { props }
   * */
  overrideToolbar = (props): JSX.Element => {
    return <CustomTableToolbar {...props} selectAllComponent={this.props.selectAllComponent} />;
  };

  /**
   * Override the default MT table row with a custom one, and render it.
   *
   * @param { props }
   * */
  overrideTableRow = (props): JSX.Element => {
    const rowData = props.data;
    const isTreeExpanded = this.expandedTrees.includes(rowData.id)
      ? true
      : rowData.tableData.isTreeExpanded;

    if (isTreeExpanded !== rowData.tableData.isTreeExpanded) {
      this.props.tableRef.current.dataManager.changeTreeExpand(rowData.tableData.path);
    }

    return <CustomTableRow {...props} onTreeExpandClick={this.onTreeExpandClick} />;
  };

  render() {
    const { disableBorders, components, fullWidth, nested, tableRootStyle, ...rest } = this.props;

    const themeConfig: ThemeOptions = {
      palette,
      overrides: {
        MuiTable: {
          root: {
            ...tableRootStyle
          }
        }
      }
    };

    if (disableBorders) {
      themeConfig['overrides'] = {
        ...themeConfig.overrides,
        MuiTableCell: { root: { borderBottom: 0 } }
      };
    }

    const theme = createMuiTheme(themeConfig);

    return (
      <MuiThemeProvider theme={theme}>
        <MaterialTable
          icons={TableIcons}
          parentChildData={
            // @ts-ignore
            !nested ? undefined : (row, rows) => rows.find((a) => a.id === row.parentId)
          }
          components={{
            Toolbar: this.overrideToolbar,
            Row: this.overrideTableRow,
            Container: (props) => (
              <Paper
                {...props}
                style={{
                  ...props.style,
                  position: 'relative',
                  width: fullWidth ? '100%' : undefined
                }}
                elevation={0}
              />
            ),
            ...components
          }}
          localization={this.localization}
          {...rest}
        />
      </MuiThemeProvider>
    );
  }
}

export default injectIntl(AlteredMaterialTable);
